import {
  Palette,
  BarChart3,
  Target,
  Rocket,
  TrendingUp,
  CalendarDays,
  Search,
  Clapperboard,
  type LucideIcon,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";

type Panel = {
  icon: LucideIcon;
  titleKey: string;
  noteKey?: string;
  prefix: string;
  count: number;
};

const PANELS: Panel[] = [
  { icon: Palette, titleKey: "fx.cat.adCreation", prefix: "fx.adCreation", count: 4 },
  { icon: BarChart3, titleKey: "fx.cat.campaign", prefix: "fx.campaign", count: 7 },
  { icon: Target, titleKey: "fx.cat.audience", prefix: "fx.audience", count: 3 },
  { icon: Rocket, titleKey: "fx.cat.goals", prefix: "fx.goals", count: 8 },
  { icon: TrendingUp, titleKey: "fx.cat.analytics", prefix: "fx.analytics", count: 6 },
  { icon: CalendarDays, titleKey: "fx.cat.content", prefix: "fx.content", count: 3 },
  { icon: Search, titleKey: "fx.cat.competitor", prefix: "fx.competitor", count: 4 },
  { icon: Clapperboard, titleKey: "fx.cat.video", noteKey: "fx.video.note", prefix: "fx.video", count: 8 },
];

export function FeaturesExplained() {
  const { t } = useI18n();
  return (
    <section id="features-explained" className="py-20 md:py-24 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
            {t("fx.title.1")}{" "}
            <span className="text-gradient-orange">{t("fx.title.2")}</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("fx.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {PANELS.map(({ icon: Icon, titleKey, noteKey, prefix, count }) => (
            <div
              key={titleKey}
              className="rounded-3xl border border-border bg-card p-6 md:p-8 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg md:text-xl font-bold tracking-tight">
                  {t(titleKey)}
                </h3>
              </div>
              {noteKey && (
                <p className="text-xs md:text-sm font-semibold text-primary bg-primary/10 border border-primary/20 rounded-full px-3 py-1.5 mb-4 self-start">
                  {t(noteKey)}
                </p>
              )}
              <ul className="space-y-3 md:space-y-3.5">
                {Array.from({ length: count }).map((_, i) => {
                  const n = i + 1;
                  return (
                    <li key={`${prefix}.${n}`} className="flex gap-3 text-base">
                      <span className="text-primary shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-primary" />
                      <span className="text-muted-foreground leading-relaxed">
                        <span className="font-semibold text-foreground">
                          {t(`${prefix}.${n}.name`)}
                        </span>{" "}
                        — {t(`${prefix}.${n}.desc`)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
